import { RestClientService } from '../rest-client.service';
import { Location } from '@angular/common';
import { environment } from '../../environments/environment';
import { catchError, switchMap } from 'rxjs/operators';
import { ObservableResult } from '../../models/result.model';
import { LeadsDataService, LeadsPageInfo } from './leads-data.service';
import {
  Ausschreibungstextes, Broschures, CadDatens,
  CheckDuplicateContact, FatcsStatus, ConstructionTypes,
  CreateLead, EmailData, ExtendedProperties, IAddressData,
  IContact,
  LeadData, LeadDocumentsModel,
  LeadsData, LeadsDataAction, LeadsHistory,
  LeadsQueryRequest,
  LeadSources, LeadStatus,
  LeadType, Musters, SolveDuplicateContact,
  Articles, Keyword, ImportComposerLeadResponse,
  ImportComposerLeadRequest
} from '../../models/leads/leads.model';

export class ApiLeadsDataService implements LeadsDataService {

  constructor(
    private client: RestClientService,
  ) {
  }


  getLeads(onlyMyLeads: boolean, openLeads: boolean, closedLeads: boolean, unreadLeads: boolean, pageInfo: LeadsPageInfo): ObservableResult<LeadsData[]> {
    const url = '/api/Leads';
    const method = 'GET';
    const body = {
      onlyMyLeads: `${onlyMyLeads}`,
      openLeads: `${openLeads}`,
      closedLeads: `${closedLeads}`,
      onlyUnread: `${unreadLeads}`,
        ...pageInfo
    };

    return this.client.requestWithConstUrl<LeadsData[]>(method, url, body);
  }


  getLeadsCount(onlyMyLeads: boolean, openLeads: boolean, closedLeads: boolean, unreadLeads: boolean, pageInfo: LeadsPageInfo): ObservableResult<number> {
    const url = '/api/Leads/Count';
    const method = 'GET';
    const body = {
      onlyMyLeads: `${onlyMyLeads}`,
      openLeads: `${openLeads}`,
      closedLeads: `${closedLeads}`,
      onlyUnread: `${unreadLeads}`,
        ...pageInfo
    };

    return this.client.requestWithConstUrl<number>(method, url, body);
  }

  queryLeads(request: LeadsQueryRequest): ObservableResult<LeadsData[]> {
    const url = '/api/Leads/Query';
    const method = 'POST';

    return this.client.requestWithConstUrl<LeadsData[]>(method, url, request);
  }

  queryLeadsCount(request: LeadsQueryRequest): ObservableResult<number> {
    const url = '/api/Leads/QueryCount';
    const method = 'POST';

    return this.client.requestWithConstUrl<number>(method, url, request);
  }

  queryLeadsDownload(request: LeadsQueryRequest): ObservableResult<Blob> {
    const url = '/api/Leads/QueryDownload';
    const method = 'POST';
    return this.client.requestWithConstUrl<Blob>(method, url, request, undefined, undefined, true, true);
  }

  addLead(body: IContact): ObservableResult<0> {
    const url = '/api/Leads';
    const method = 'POST';

    return this.client.requestWithConstUrl<0>(method, url, body);
  }

  createLead(request: CreateLead): ObservableResult<number> {
    const url = '/api/Leads';
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url, request);
  }

  getLeadById(leadId: number): ObservableResult<LeadData> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '', '/contacts/do-request'
    );

    const request = {
      method: 'GET',
      uri: `/api/Leads/${leadId}`
    };

    return this.client.post<{data: LeadData}>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body.data)),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })
    );
  }

  getLeadWithContactById(contactId: number): ObservableResult<LeadData[]> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '', '/contacts/do-request'
    );

    const request = {
      method: 'GET',
      uri: `/api/Leads/WithContact/${contactId}`
    };

    return this.client.post<{data: LeadData[]}>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body.data)),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })

    );
  }


  getLeadTypes(): ObservableResult<LeadType[]> {
    const url = '/api/Leads/LeadTypes';
    const method = 'GET';

    return this.client.requestWithConstUrl<LeadType[]>(method, url, { 'filterByOUN': 'false' });
  }

  getLeadStatus(): ObservableResult<LeadStatus[]> {
    const url = '/api/Leads/LeadStatus';
    const method = 'GET';

    return this.client.requestWithConstUrl<LeadStatus[]>(method, url);
  }

  getLeadSources(): ObservableResult<LeadSources[]> {
    const url = '/api/Leads/LeadSources';
    const method = 'GET';

    return this.client.requestWithConstUrl<LeadSources[]>(method, url);
  }

  getLeadFacts(id: number): ObservableResult<FatcsStatus[]> {
    const url = `/api/Leads/Facts/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl<FatcsStatus[]>(method, url);
  }

  getLeadHistory(id: number): ObservableResult<LeadsHistory[]> {
    const url = `/api/Leads/History/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getLeadComposerOffers(id: number): ObservableResult<any[]> {
    const url = `/api/Leads/ComposerOffers/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getLeadDocuments(id: number): ObservableResult<LeadDocumentsModel[]> {
    const url = `/api/Leads/Documents/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  markAsRead(id: number): ObservableResult<void> {
    const url = `/api/Leads/MarkAsRead/${id}`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url);
  }

  markMessageAsRead(id: number): ObservableResult<void> {
    const url = `/api/Message/MarkAsRead/${id}`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url);
  }

  markAsUnread(id: number): ObservableResult<void> {
    const url = `/api/Leads/MarkAsUnRead/${id}`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url);
  }

  removeLeadDocument(id: number): ObservableResult<void> {
    const url = `/api/Leads/Document/${id}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl(method, url);
  }

  markLeadDocument(id: number, favorite: boolean): ObservableResult<void> {
    const url = `/api/Leads/MarkLeadDocument/${id}?favorite=${favorite}`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url);
  }

  downloadLeadDocument(id: number): ObservableResult<Blob> {
    const url = `/api/Leads/Document/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url, undefined, undefined, undefined, true, true);
  }

  downloadLeadDocuments(id: number): ObservableResult<Blob> {
    const url = `/api/Leads/Documents/${id}/Download`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url, undefined, undefined, undefined, true, true);
  }

  downloadLeadList(): ObservableResult<Blob> {
    const url = `/api/Leads/List`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url, undefined, undefined, undefined, true, true);
  }

  downloadSearchedLeadByIds(reqBody: number[]): ObservableResult<Blob> {
    const url = `/api/Leads/ListLeads`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url, reqBody, undefined, undefined, true, true);
  }

  downloadLead(id: number): ObservableResult<Blob> {
    const url = `/api/Leads/List/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url, undefined, undefined, undefined, true, true);
  }

  updateAddress(id: number, address: IAddressData): ObservableResult<void> {
    const url = `/api/Leads/Address/${id}`;
    const method = 'PUT';

    return this.client.requestWithConstUrl<void>(method, url, address);
  }

  updateCommission(id: number, commission: {[key: string]: string}): ObservableResult<void> {
    const url = `/api/Leads/${id}`;
    const method = 'PUT';

    return this.client.requestWithConstUrl<void>(method, url, commission);
  }

  deleteAddress(id: number): ObservableResult<void> {
    const url = `/api/Leads/Address/${id}`;
    const method = 'DELETE';
    return this.client.requestWithConstUrl<void>(method, url);
  }

  deleteLead(id: number): ObservableResult<any> {
    const url = `/api/Leads/${id}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl(method, url);
  }

  getDuplicateLeads(): ObservableResult<void> {
    const url = `/api/Leads/Duplicates`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getDuplicateLeadById(leadId: number): ObservableResult<CheckDuplicateContact> {

    const url = `/api/Leads/Duplicates/${leadId}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  solveDuplicates(leadId: number, body: SolveDuplicateContact): ObservableResult<any> {

    const url = `/api/Leads/Duplicates/Solve/${leadId}`;
    const method = 'POST';


    return this.client.requestWithConstUrl(method, url, body);
  }

  getExtendedPropertiesByLeadId(leadId: number) {

    const url = `/api/Leads/ExtendedProperties/${leadId}`;
    const method = 'GET';


    return this.client.requestWithConstUrl(method, url);
  }

  putExtendedPropertiesByLeadId(leadId: number, extendedProperties: ExtendedProperties) {

    const url = `/api/Leads/ExtendedProperties/${leadId}`;
    const method = 'PUT';

    return this.client.requestWithConstUrl(method, url, extendedProperties);
  }

  getActionsById(id: number): ObservableResult<LeadsDataAction[]> {
    const url = `/api/Leads/NextSteps/${id}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getConstructionTypes(): ObservableResult<ConstructionTypes[]> {
    const url = `/api/Leads/ConstructionTypes`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  importComposerLead(body: ImportComposerLeadRequest): ObservableResult<ImportComposerLeadResponse> {
    const url = `/api/Leads/ImportComposerLead`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url, body);
  }

  getEmailData(kkz: string): ObservableResult<EmailData> {
    const url = `/api/Action/Studio/${kkz}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }


  putLeadById(id: number, constructionTypeId: number): ObservableResult<void> {
    const url = `/api/Leads/${id}`;
    const method = 'PUT';
    const requestBody = { ConstructionTypeId: constructionTypeId };

    return this.client.requestWithConstUrl(method, url, requestBody);

  }

  addLeadArticle( id: number, article: Articles ): ObservableResult<void> {
    const url = `/api/Leads/${id}/Article`;
    const method = 'POST';

    return this.client.requestWithConstUrl(method, url, article);

  }

  deleteLeadArticle(id: number, articleId: number): ObservableResult<void> {
    const url = `/api/Leads/${id}/Article/${articleId}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl(method, url);
  }

  updateHistoryComment(historyId: number, newComment: string): ObservableResult<void> {
    const url = `/api/Action/EditComment/${historyId}?newComment=${encodeURIComponent(newComment)}`;
    const method = 'POST';

    return this.client.requestWithConstUrl<void>(method, url);
  }

  deleteHistoryComment(historyId: number): ObservableResult<void> {
    const url = `/api/Action/DeleteComment/${historyId}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl<void>(method, url);
  }

  getBrochures(locale?: string): ObservableResult<Broschures[]> {
    const url = environment.apiRootUrl + 'contacts/brochure-list' + (locale !== undefined ? `?lang=${locale}` : '');

    return this.client.get<any>(url).pipe(
      switchMap(({ body }) => {
        const brochures: Broschures[] = [];

        for (const key in body) {
          brochures.push({
            key,
            title: body[key].title,
            img: body[key].img,
            ordernumber: body[key].ordernumber,
          });
        }

        return ObservableResult.ofSuccess(brochures);
      }),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })

    );
  }

  getAusschreibungstextes(locale?: string): ObservableResult<Broschures[]> {
    const url = environment.apiRootUrl + 'contacts/ausschreibung-list' + (locale !== undefined ? `?lang=${locale}` : '');

    return this.client.get<any>(url).pipe(
      switchMap(({ body }) => {
        const ausschreibungstextes: Ausschreibungstextes[] = [];

        for (const key in body) {
          ausschreibungstextes.push({
            key,
            title: body[key].title,
            img: body[key].img,
            ordernumber: body[key].ordernumber,
          });
        }

        return ObservableResult.ofSuccess(ausschreibungstextes);
      }),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })
    );
  }

  getCadDatens(locale?: string): ObservableResult<Broschures[]> {
    const url = environment.apiRootUrl + 'contacts/cad-daten-list' + (locale !== undefined ? `?lang=${locale}` : '');

    return this.client.get<any>(url).pipe(
      switchMap(({ body }) => {
        const cadDatens: CadDatens[] = [];

        for (const key in body) {
          cadDatens.push({
            key,
            title: body[key].title,
            img: body[key].img,
            ordernumber: body[key].ordernumber,
          });
        }

        return ObservableResult.ofSuccess(cadDatens);
      }),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })
    );
  }

  getMusters(locale?: string): ObservableResult<Musters[]> {
    const url = environment.apiRootUrl + 'contacts/master-list' + (locale !== undefined ? `?lang=${locale}` : '');

    return this.client.get<any>(url).pipe(
      switchMap(({ body }) => {
        const musters: Musters[] = [];

        for (const key in body) {
          musters.push({
            key,
            title: body[key].title,
            img: body[key].img,
          });
        }

        return ObservableResult.ofSuccess(musters);
      }),
      catchError((err) => {
        const errorBody = err.error;

        return ObservableResult.ofError(
          errorBody?.data ?? errorBody ?? 'Unknown error',
          err.status,
          errorBody?.status,
          errorBody?.error_key,
          errorBody
        );
      })
    );
  }

  getKeywords(leadId: number): ObservableResult<Keyword[]> {
    const url = `/api/Keywords/Lead/${leadId}`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  getAllKeywords(): ObservableResult<string[]> {
    const url = `/api/Keywords/`;
    const method = 'GET';

    return this.client.requestWithConstUrl(method, url);
  }

  sendKeyWords(keywords: string[], leadId: number): ObservableResult<void> {
    const url = `/api/Keywords/Lead/${leadId}`;
    const method = 'PUT';

    return this.client.requestWithConstUrl(method, url, keywords);
  }

  deleteKeywords(keywords: string[], leadId: number): ObservableResult<string[]> {
    const url = `/api/Keywords/Lead/${leadId}`;
    const method = 'DELETE';

    return this.client.requestWithConstUrl(method, url, keywords);
  }
}
